import styled from '@emotion/styled';
import { css } from '@emotion/react';

import { Heading1, IconAndTextButton, Text } from '@blueprint/ui';
import {
    ContentfulImageWrapper,
    ContentfulImage
} from '../../queries/heroContent.types';

const MIN_IMAGE_HEIGHT = '302px';

type Props = {
    src: ContentfulImage['url'];
    width: ContentfulImage['width'];
    height: ContentfulImage['height'];
    focalPoint: ContentfulImageWrapper['focalPoint']['focalPoint'] | undefined;
};
const getBackgroundPosition = (xy: number, focalPoint: number) => {
    const offset = (100 / xy) * focalPoint;
    const offfsetPosition = 100 - offset;
    const result = `${offfsetPosition}%`;

    return result;
};

export const HeroImage = styled('div')<Props>(
    ({ theme: { borderRadius, mq }, src, width, height, focalPoint }) => {
        const backgroundPositionY = focalPoint?.y
            ? `${getBackgroundPosition(height, focalPoint?.y)}`
            : 'center';
        const backgroundPositionX = focalPoint?.x
            ? `${getBackgroundPosition(width, focalPoint?.x)}`
            : 'center';

        return css`
            background-image: url(${src}?w=400&h=500);
            background-position: ${backgroundPositionY} ${backgroundPositionX};
            background-repeat: no-repeat;
            border-radius: ${borderRadius['4px']};
            background-size: cover;

            position: absolute;
            height: 100%;
            width: 100%;
            z-index: -1;
            bottom: 0;

            ${mq.small} {
                background-image: url(${src}?w=1000&h=600);
                background-size: auto;
            }

            ${mq.large} {
                background-image: url(${src}?w=${width || 1200}&h=${height ||
                600});
                background-size: auto;
            }
        `;
    }
);

export const SearchInitiationWrapper = styled.div(
    ({ theme: { spacing, borderRadius, mq } }) => css`
        position: relative;
        display: flex;
        flex-direction: column;
        width: 100%;
        margin-top: ${spacing['8px']};
        align-items: center;
        font-size: 30px;
        border-radius: ${borderRadius['4px']};
        padding-left: ${spacing['16px']};
        padding-right: ${spacing['16px']};
        padding-bottom: ${spacing['64px']};
        overflow: hidden;

        ${mq.small} {
            padding-bottom: 0;
            padding-left: ${spacing['24px']};
            padding-right: ${spacing['24px']};
            height: ${MIN_IMAGE_HEIGHT};
        }

        ${mq.large} {
            height: 600px;
            border-radius: ${borderRadius['24px']};
        }
    `
);

export const SearchInputWrapper = styled.div(
    ({ theme: { mq, spacing } }) => css`
        display: none;

        ${mq.large} {
            display: flex;
            width: 100%;
            flex-direction: column;
            max-width: 983px;
            padding-top: ${spacing['32px']};
        }
    `
);

export const SearchBarWrapper = styled.div(
    ({ theme: { spacing } }) => css`
        margin-top: ${spacing['16px']};
    `
);

export const SearchInitiation = styled.div(
    ({ theme: { colors, borderRadius } }) => css`
        display: flex;
        width: 100%;
        background: ${colors.white};
        border-radius: ${borderRadius['4px']};
        height: 72px;
    `
);

export const MainHeadlineMobile = styled(Text)(
    ({ theme: { mq, spacing, fontSizes } }) => css`
        font-family: 'ZooplaDisplay';
        font-weight: bold;
        display: flex;
        align-self: flex-start;
        font-size: ${fontSizes['4']};
        margin-top: ${spacing['32px']};

        ${mq.large} {
            display: none;
        }
    `
);

export const MainHeadlineDesktop = styled(Text)(
    ({ theme: { mq, spacing } }) => css`
        display: none;

        ${mq.large} {
            font-family: 'ZooplaDisplay';
            font-weight: bold;
            margin-top: ${spacing['32px']};
            display: flex;
            font-size: 68px;
        }
    `
);

export const SearchInitiationButton = styled(IconAndTextButton)(
    ({ theme: { colors, borderRadius, mq, spacing } }) => css`
        width: 100%;
        display: flex;
        text-align: center;
        background-color: ${colors['primary-brand']};
        padding: 0;
        height: ${spacing['48px']};
        min-height: ${spacing['48px']};
        border-radius: ${borderRadius['4px']};
        margin-top: ${spacing['24px']};

        &:hover {
            background-color: ${colors['primary-light']};
        }

        ${mq.large} {
            display: none;
        }
    `
);

export const SearchInputTitle = styled(Heading1)(
    ({ theme: { mq, fontSizes, spacing } }) => css`
        display: none;
        font-size: ${fontSizes[9]};

        ${mq.large} {
            display: flex;
            align-self: flex-start;
            padding-top: ${spacing['32px']};
            margin-bottom: ${spacing['32px']};
        }
    `
);
